import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import { Badge } from "@/design-system/primitives";
import { useClinicalPatientSearch } from "@/lib/api/clinicalAdminHooks";
import {
  STAGE_FILTERS,
  type SupportTicket,
  TICKET_STATES,
  TICKET_STATE_LABELS,
  ticketDisplaySubject,
  ticketInDateRange,
  ticketMatchesQuery,
  useAdminRoster,
  useCreateTicket,
  useCreateView,
  useResolveTicket,
  useRunTicketSweep,
  useTickets,
  useUpdateTicket,
} from "@/lib/api/ticketHooks";
import type { PatientCard } from "@/types/clinicalAdmin";

import { TicketDrawer } from "./TicketDrawer";
import {
  DISPOSITION_TONE,
  DISPOSITIONS,
  ageLabel,
  isOverdue,
  canSetGenericTicketState,
  requiresClinicalResolution,
} from "./ticketUi";
import {
  ClinicalAgentDrawer,
  type ClinicalAgentContext,
} from "../clinical-admin/ClinicalAgentDrawer";
import { PatientPreviewDrawer } from "../clinical-admin/PatientPreviewDrawer";

// Kanban is a triage surface; with filters the working set is small. Cap cards
// per column so a large unfiltered backlog can't tank the DOM — overflow spills
// to the List view, which scales to hundreds of rows cheaply (plain table).
const COLUMN_CAP = 40;

type View = "kanban" | "list";

/**
 * Support & Exception Board — order exceptions / stuck orders.
 * Kanban (drag to change state) + List (dense, scales to hundreds) views over
 * the same filtered ticket set. Populated by the backend detection sweeps.
 */
const VIEW_STORAGE_KEY = "support.board.view";

export function SupportBoardScreen() {
  // View survives navigation within the session; filters live in the URL so
  // Overview stages (and colleagues' links) can deep-link a pre-filtered board.
  const [view, setViewState] = useState<View>(() => {
    if (typeof window === "undefined") return "kanban";
    return (
      (window.sessionStorage.getItem(VIEW_STORAGE_KEY) as View) || "kanban"
    );
  });
  const setView = (v: View) => {
    setViewState(v);
    try {
      window.sessionStorage.setItem(VIEW_STORAGE_KEY, v);
    } catch {
      /* private mode — fine */
    }
  };

  const [params, setParams] = useSearchParams();
  const assignee = params.get("assignee") ?? "";
  const disposition = params.get("disposition") ?? "";
  const stage = params.get("stage") ?? "";
  const query = params.get("q") ?? "";
  const overdue = params.get("overdue") === "1";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const deepLinkedTicketId = params.get("ticket");
  const [openTicketId, setOpenTicketId] = useState<string | null>(
    deepLinkedTicketId,
  );
  const [patientPreviewId, setPatientPreviewId] = useState<string | null>(null);
  const [agentContext, setAgentContext] = useState<ClinicalAgentContext | null>(
    null,
  );
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (deepLinkedTicketId) setOpenTicketId(deepLinkedTicketId);
  }, [deepLinkedTicketId]);

  const { data, isLoading, error } = useTickets({
    assignee: assignee || undefined,
    disposition: disposition || undefined,
    overdue: overdue || undefined,
  });
  const roster = useAdminRoster();
  const sweep = useRunTicketSweep();

  const stageFilter = STAGE_FILTERS.find((s) => s.key === stage);
  const tickets = useMemo(() => {
    let rows = data?.tickets ?? [];
    if (stageFilter) rows = rows.filter(stageFilter.match);
    if (query) rows = rows.filter((t) => ticketMatchesQuery(t, query));
    if (from || to) rows = rows.filter((t) => ticketInDateRange(t, from, to));
    return rows;
  }, [data, stageFilter, query, from, to]);
  const nameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const a of roster.data?.admins ?? [])
      m[a.id] = a.display_name || a.email || a.id;
    return m;
  }, [roster.data]);

  return (
    <div className="flex flex-col gap-20">
      <div className="flex items-center gap-15 flex-wrap">
        <h1 className="type-headline-md">Support &amp; Exceptions</h1>
        <span className="type-label-sm text-ink-muted">
          {tickets.length} open
        </span>
        <div className="ml-auto flex items-center gap-10">
          <div className="flex rounded border border-line overflow-hidden">
            {(["kanban", "list"] as View[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`type-label-sm px-12 py-6 capitalize ${
                  view === v
                    ? "bg-raised text-ink"
                    : "text-ink-muted hover:bg-raised/50"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => sweep.mutate()}
            disabled={sweep.isPending}
            className="type-label-sm px-12 py-8 rounded border border-line hover:bg-raised disabled:opacity-50"
          >
            {sweep.isPending ? "Sweeping…" : "Run sweep"}
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="type-label-sm px-12 py-8 rounded bg-ink text-page hover:opacity-90"
          >
            New ticket
          </button>
        </div>
      </div>

      {/* Filters — URL-synced (deep-linkable from Overview stages) */}
      <div className="flex items-center gap-10 flex-wrap">
        <input
          type="search"
          value={query}
          onChange={(e) => setFilter("q", e.target.value)}
          placeholder="Search tickets, customers, reasons…"
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-[22rem] max-w-full"
          aria-label="Search tickets"
        />
        <select
          value={stage}
          onChange={(e) => setFilter("stage", e.target.value)}
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised"
          aria-label="Filter by stage"
        >
          <option value="">All stages</option>
          {STAGE_FILTERS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          value={assignee}
          onChange={(e) => setFilter("assignee", e.target.value)}
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised"
          aria-label="Filter by assignee"
        >
          <option value="">All assignees</option>
          <option value="me">My tasks</option>
          <option value="unassigned">Unassigned</option>
          {(roster.data?.admins ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.display_name || a.email || a.id}
            </option>
          ))}
        </select>
        <select
          value={disposition}
          onChange={(e) => setFilter("disposition", e.target.value)}
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised"
          aria-label="Filter by disposition"
        >
          <option value="">All dispositions</option>
          {DISPOSITIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-6 type-label-sm text-ink-muted">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => setFilter("overdue", e.target.checked ? "1" : "")}
          />
          Overdue only
        </label>
        <label className="flex items-center gap-6 type-label-sm text-ink-muted">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFilter("from", e.target.value)}
            className="px-8 py-6 rounded border border-line bg-raised"
            aria-label="Flagged from date"
          />
        </label>
        <label className="flex items-center gap-6 type-label-sm text-ink-muted">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setFilter("to", e.target.value)}
            className="px-8 py-6 rounded border border-line bg-raised"
            aria-label="Flagged to date"
          />
        </label>
        {(query ||
          stage ||
          assignee ||
          disposition ||
          overdue ||
          from ||
          to) && (
          <>
            <button
              type="button"
              onClick={() =>
                setParams(new URLSearchParams(), { replace: true })
              }
              className="type-label-sm text-accent hover:underline"
            >
              Clear filters
            </button>
            <SaveViewButton
              filters={{
                q: query,
                stage,
                assignee,
                disposition,
                overdue: overdue ? "1" : "",
                from,
                to,
              }}
              viewMode={view}
            />
          </>
        )}
      </div>

      {isLoading ? (
        <div className="type-label-sm text-ink-muted py-40">
          Loading tickets…
        </div>
      ) : error ? (
        <div className="type-label-sm text-danger py-40">
          Failed to load tickets.
        </div>
      ) : view === "kanban" ? (
        <KanbanView
          tickets={tickets}
          onCardClick={setOpenTicketId}
          onOverflow={() => setView("list")}
        />
      ) : (
        <ListView
          tickets={tickets}
          nameById={nameById}
          onRowClick={setOpenTicketId}
          onOpenPatient={setPatientPreviewId}
        />
      )}

      <TicketDrawer
        ticketId={openTicketId}
        onClose={() => setOpenTicketId(null)}
        onOpenPatient={setPatientPreviewId}
        onOpenAgent={setAgentContext}
      />
      <PatientPreviewDrawer
        patientId={patientPreviewId}
        onClose={() => setPatientPreviewId(null)}
        onOpenTicket={(ticketId) => {
          setPatientPreviewId(null);
          setOpenTicketId(ticketId);
        }}
        onOpenAgent={setAgentContext}
      />
      <ClinicalAgentDrawer
        open={Boolean(agentContext)}
        onClose={() => setAgentContext(null)}
        context={agentContext ?? {}}
      />
      {createOpen && (
        <NewTicketModal
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            setOpenTicketId(id);
          }}
        />
      )}
    </div>
  );
}

/* ---------- Save view ---------- */

function SaveViewButton({
  filters,
  viewMode,
}: {
  filters: Record<string, string>;
  viewMode: string;
}) {
  const create = useCreateView();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);

  const submit = () => {
    if (!name.trim() || create.isPending) return;
    const cleaned = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v),
    );
    create.mutate(
      { name: name.trim(), shared, filters: cleaned, view_mode: viewMode },
      {
        onSuccess: () => {
          setOpen(false);
          setName("");
          setShared(false);
        },
      },
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="type-label-sm px-10 py-6 rounded border border-accent text-accent bg-accent-wash hover:opacity-90"
      >
        Save view
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[80]"
          role="dialog"
          aria-modal="true"
          aria-label="Save view"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,26rem)] bg-page border border-line rounded shadow-lg p-20 flex flex-col gap-12">
            <div className="type-label-md font-semibold">
              Save current filters as a view
            </div>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape") setOpen(false);
              }}
              placeholder="View name (e.g. Overdue clinical)"
              className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
            />
            <div className="flex items-center gap-15">
              <label className="flex items-center gap-6 type-label-sm">
                <input
                  type="radio"
                  checked={!shared}
                  onChange={() => setShared(false)}
                />
                Personal (only you)
              </label>
              <label className="flex items-center gap-6 type-label-sm">
                <input
                  type="radio"
                  checked={shared}
                  onChange={() => setShared(true)}
                />
                Shared (all admins)
              </label>
            </div>
            {create.isError && (
              <div className="type-label-sm text-danger">
                Failed to save view.
              </div>
            )}
            <div className="flex items-center justify-end gap-8">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="type-label-sm px-12 py-8 rounded border border-line hover:bg-raised"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!name.trim() || create.isPending}
                className="type-label-sm px-12 py-8 rounded bg-ink text-page hover:opacity-90 disabled:opacity-50"
              >
                {create.isPending ? "Saving…" : "Save view"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------- New ticket ---------- */

function NewTicketModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const create = useCreateTicket();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [disposition, setDisposition] = useState("ops");
  const [orderId, setOrderId] = useState("");
  const [patientQuery, setPatientQuery] = useState("");
  const [selectedPatient, setSelectedPatient] = useState<PatientCard | null>(
    null,
  );
  const patientSearch = useClinicalPatientSearch(
    patientQuery,
    patientQuery.trim().length >= 2 && !selectedPatient,
  );

  const submit = () => {
    if (!title.trim() || create.isPending) return;
    create.mutate(
      {
        title: title.trim(),
        body: body.trim() || undefined,
        disposition,
        pharmacy_order_id: orderId.trim() || undefined,
        patient_id: selectedPatient?.id,
      },
      { onSuccess: (t) => (t?.id ? onCreated(t.id) : onClose()) },
    );
  };

  return (
    <div
      className="fixed inset-0 z-[80]"
      role="dialog"
      aria-modal="true"
      aria-label="New ticket"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(92vw,32rem)] bg-page border border-line rounded shadow-lg p-20 flex flex-col gap-12">
        <div className="type-label-md font-semibold">New ticket</div>
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onClose();
          }}
          placeholder="Title (required)"
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Details (optional)"
          rows={3}
          className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
        />
        <div className="flex items-center gap-8">
          <select
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
            className="type-label-sm px-10 py-8 rounded border border-line bg-raised"
            aria-label="Disposition"
          >
            {DISPOSITIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <input
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Order id (optional)"
            className="type-label-sm px-10 py-8 rounded border border-line bg-raised flex-1"
          />
        </div>
        <div className="relative">
          <input
            type="search"
            value={patientQuery}
            onChange={(event) => {
              setPatientQuery(event.target.value);
              setSelectedPatient(null);
            }}
            placeholder="Search patient name or email (optional)"
            aria-label="Link patient"
            autoComplete="off"
            className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
          />
          {selectedPatient ? (
            <div className="mt-6 flex items-center gap-8 rounded border border-accent/30 bg-accent-wash px-10 py-8">
              <div className="min-w-0 flex-1">
                <p className="type-label-sm font-semibold truncate">
                  {selectedPatient.display_name || selectedPatient.email}
                </p>
                {selectedPatient.display_name && selectedPatient.email ? (
                  <p className="text-10 text-ink-muted truncate">
                    {selectedPatient.email}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedPatient(null);
                  setPatientQuery("");
                }}
                className="text-10 text-accent hover:underline"
              >
                Change
              </button>
            </div>
          ) : patientQuery.trim().length >= 2 ? (
            <div className="absolute z-10 mt-2 max-h-48 w-full overflow-y-auto rounded border border-line bg-page shadow-lg">
              {patientSearch.isLoading ? (
                <p className="px-10 py-8 type-label-sm text-ink-muted">
                  Searching…
                </p>
              ) : (patientSearch.data?.results ?? []).length ? (
                (patientSearch.data?.results ?? []).map((patient) => (
                  <button
                    key={patient.id}
                    type="button"
                    onClick={() => {
                      setSelectedPatient(patient);
                      setPatientQuery(
                        patient.display_name || patient.email || patient.id,
                      );
                    }}
                    className="block w-full border-b border-line px-10 py-8 text-left last:border-b-0 hover:bg-raised"
                  >
                    <span className="block type-label-sm font-semibold">
                      {patient.display_name || "Unnamed patient"}
                    </span>
                    <span className="block text-10 text-ink-muted">
                      {patient.email || "No email"}
                    </span>
                  </button>
                ))
              ) : (
                <p className="px-10 py-8 type-label-sm text-ink-muted">
                  No patients found.
                </p>
              )}
            </div>
          ) : null}
        </div>
        {create.isError && (
          <div className="type-label-sm text-danger">
            Failed to create ticket. Check the linked order and try again.
          </div>
        )}
        <div className="flex items-center justify-end gap-8">
          <button
            type="button"
            onClick={onClose}
            className="type-label-sm px-12 py-8 rounded border border-line hover:bg-raised"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!title.trim() || create.isPending}
            className="type-label-sm px-12 py-8 rounded bg-ink text-page hover:opacity-90 disabled:opacity-50"
          >
            {create.isPending ? "Creating…" : "Create ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Kanban ---------- */

function KanbanView({
  tickets,
  onCardClick,
  onOverflow,
}: {
  tickets: SupportTicket[];
  onCardClick: (id: string) => void;
  onOverflow: () => void;
}) {
  const update = useUpdateTicket();
  const byState = useMemo(() => {
    const g: Record<string, SupportTicket[]> = {};
    for (const s of TICKET_STATES) g[s] = [];
    for (const t of tickets) (g[t.state] ??= []).push(t);
    return g;
  }, [tickets]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over) return;
    const id = String(active.id);
    const newState = String(over.id);
    const t = tickets.find((x) => x.id === id);
    if (
      t &&
      t.state !== newState &&
      (TICKET_STATES as readonly string[]).includes(newState)
    ) {
      if (!canSetGenericTicketState(t, newState)) {
        onCardClick(id);
        return;
      }
      update.mutate({ id, state: newState });
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
        {TICKET_STATES.map((s) => (
          <Column
            key={s}
            state={s}
            tickets={byState[s] ?? []}
            onCardClick={onCardClick}
            onOverflow={onOverflow}
          />
        ))}
      </div>
    </DndContext>
  );
}

function Column({
  state,
  tickets,
  onCardClick,
  onOverflow,
}: {
  state: string;
  tickets: SupportTicket[];
  onCardClick: (id: string) => void;
  onOverflow: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: state });
  const shown = tickets.slice(0, COLUMN_CAP);
  const overflow = tickets.length - shown.length;
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col gap-10 rounded border p-10 min-h-[12rem] transition-colors ${
        isOver ? "border-accent bg-accent-wash/40" : "border-line bg-sunken/40"
      }`}
    >
      <div className="flex items-center justify-between px-2">
        <span className="type-label-sm font-semibold">
          {TICKET_STATE_LABELS[state]}
        </span>
        <span className="text-10 text-ink-muted">{tickets.length}</span>
      </div>
      {shown.map((t) => (
        <TicketCard key={t.id} ticket={t} onClick={onCardClick} />
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={onOverflow}
          className="text-10 text-accent hover:underline text-left px-2 py-4"
        >
          +{overflow} more — open List view
        </button>
      )}
    </div>
  );
}

/** Missing-info cards say what the clinician actually needs, not just the
 *  reason label: their details plus the first programmatic intake check. */
function missingInfoSummary(ticket: SupportTicket): string | null {
  if (ticket.source !== "missing_info") return null;
  const parts = [
    ticket.missing_info_details?.trim(),
    ticket.missing_info_findings?.[0]?.trim(),
  ].filter((part): part is string => !!part);
  return parts.length ? parts.join(" · ") : null;
}

function TicketCard({
  ticket,
  onClick,
}: {
  ticket: SupportTicket;
  onClick: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: ticket.id });
  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 }
    : undefined;
  const overdue = isOverdue(ticket.sla_due_at);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => {
        if (!isDragging) onClick(ticket.id);
      }}
      className={`rounded border border-line bg-raised p-10 cursor-grab active:cursor-grabbing shadow-sm ${
        isDragging ? "opacity-70" : ""
      }`}
      data-testid="ticket-card"
    >
      <div className="flex items-center gap-6 mb-6 flex-wrap">
        <Badge
          tone={DISPOSITION_TONE[ticket.disposition] ?? "neutral"}
          size="sm"
        >
          {ticket.disposition}
        </Badge>
        <Badge tone={overdue ? "danger" : "neutral"} size="sm">
          {overdue ? "overdue" : ageLabel(ticket.first_flagged_at)}
        </Badge>
        {ticket.source === "aged_state" && (
          <Badge tone="info" size="sm">
            aged
          </Badge>
        )}
      </div>
      <div className="type-label-sm text-ink leading-snug line-clamp-3">
        {ticketDisplaySubject(ticket)}
      </div>
      {missingInfoSummary(ticket) && (
        <div className="mt-4 text-11 text-ink-muted leading-snug line-clamp-2">
          {missingInfoSummary(ticket)}
        </div>
      )}
      <div className="mt-6 flex items-center justify-between text-10 text-ink-muted">
        <span className="font-mono">{ticket.ticket_number}</span>
        <span>{ticket.assigned_to ? "assigned" : "unassigned"}</span>
      </div>
    </div>
  );
}

/* ---------- List (scales to hundreds; bulk-edit surface) ---------- */

function ListView({
  tickets,
  nameById,
  onRowClick,
  onOpenPatient,
}: {
  tickets: SupportTicket[];
  nameById: Record<string, string>;
  onRowClick: (id: string) => void;
  onOpenPatient: (patientId: string) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const update = useUpdateTicket();
  const resolve = useResolveTicket();
  const roster = useAdminRoster();
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [bulkState, setBulkState] = useState("");
  const [bulkResolveReason, setBulkResolveReason] = useState("");

  // Selection is scoped to what's visible: filters changing can orphan ids,
  // so operate on the intersection.
  const visibleIds = useMemo(
    () => new Set(tickets.map((t) => t.id)),
    [tickets],
  );
  const selectedIds = [...selected].filter((id) => visibleIds.has(id));
  const selectedTickets = tickets.filter((ticket) =>
    selectedIds.includes(ticket.id),
  );
  const clinicalResolutionRequired = selectedTickets.some(
    requiresClinicalResolution,
  );
  const allSelected =
    tickets.length > 0 && selectedIds.length === tickets.length;

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(tickets.map((t) => t.id)));
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const runBulk = (fn: (id: string) => void) => {
    for (const id of selectedIds) fn(id);
    setSelected(new Set());
  };

  return (
    <div className="flex flex-col gap-10">
      {selectedIds.length > 0 && (
        <div className="px-12 py-10 border border-line rounded bg-sunken/40 flex items-center gap-10 flex-wrap">
          <span className="type-label-sm font-semibold">
            {selectedIds.length} selected
          </span>
          <select
            value={bulkAssignee}
            onChange={(e) => setBulkAssignee(e.target.value)}
            className="type-label-sm px-8 py-6 rounded border border-line bg-raised"
            aria-label="Bulk assignee"
          >
            <option value="">Assign to…</option>
            <option value="__unassign__">Unassign</option>
            {(roster.data?.admins ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.display_name || a.email || a.id}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!bulkAssignee}
            onClick={() =>
              runBulk((id) =>
                bulkAssignee === "__unassign__"
                  ? update.mutate({ id, unassign: true })
                  : update.mutate({ id, assigned_to: bulkAssignee }),
              )
            }
            className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            Assign
          </button>
          <select
            value={bulkState}
            onChange={(e) => setBulkState(e.target.value)}
            className="type-label-sm px-8 py-6 rounded border border-line bg-raised"
            aria-label="Bulk state"
          >
            <option value="">Set status…</option>
            {TICKET_STATES.filter((s) =>
              selectedTickets.every((t) => canSetGenericTicketState(t, s)),
            ).map((s) => (
              <option key={s} value={s}>
                {TICKET_STATE_LABELS[s]}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={
              !bulkState ||
              !selectedTickets.every((t) =>
                canSetGenericTicketState(t, bulkState),
              )
            }
            onClick={() =>
              runBulk((id) => update.mutate({ id, state: bulkState }))
            }
            className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            Apply
          </button>
          <input
            value={bulkResolveReason}
            onChange={(event) => setBulkResolveReason(event.target.value)}
            placeholder="Force-resolve reason"
            aria-label="Bulk force-resolve reason"
            className="type-label-sm px-8 py-6 rounded border border-line bg-raised min-w-[15rem]"
          />
          <button
            type="button"
            disabled={!bulkResolveReason.trim() || clinicalResolutionRequired}
            onClick={() =>
              runBulk((id) =>
                resolve.mutate({ id, note: bulkResolveReason.trim() }),
              )
            }
            className="type-label-sm px-10 py-6 rounded border border-line hover:bg-raised disabled:opacity-40"
          >
            Force resolve
          </button>
          {clinicalResolutionRequired && (
            <span className="type-label-sm text-ink-muted">
              Open each Missing Info ticket, verify its evidence, then Resolve
              &amp; return to clinician.
            </span>
          )}
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="type-label-sm text-ink-muted hover:underline ml-auto"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-line rounded [&_th]:px-10 [&_th]:py-8 [&_td]:px-10 [&_td]:py-8">
        <table className="w-full text-left type-label-sm">
          <thead className="bg-sunken/60 text-ink-muted text-10 uppercase">
            <tr>
              <th className="w-[2rem]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all visible"
                />
              </th>
              <th>Ticket</th>
              <th>Customer</th>
              <th>Disposition</th>
              <th>State</th>
              <th>Age / SLA</th>
              <th>Source</th>
              <th>Assignee</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => {
              const overdue = isOverdue(t.sla_due_at);
              return (
                <tr
                  key={t.id}
                  onClick={() => onRowClick(t.id)}
                  className="border-t border-line hover:bg-raised cursor-pointer"
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(t.id)}
                      onChange={() => toggleOne(t.id)}
                      aria-label="Select ticket"
                    />
                  </td>
                  <td className="max-w-[28rem]">
                    <div className="font-mono text-10 text-ink-muted">
                      {t.ticket_number}
                    </div>
                    <div className="line-clamp-1">
                      {ticketDisplaySubject(t)}
                    </div>
                    {missingInfoSummary(t) && (
                      <div className="text-11 text-ink-muted line-clamp-1">
                        {missingInfoSummary(t)}
                      </div>
                    )}
                  </td>
                  <td
                    className="whitespace-nowrap"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t.patient_id ? (
                      <button
                        type="button"
                        onClick={() => onOpenPatient(t.patient_id as string)}
                        className="text-accent hover:underline"
                      >
                        {t.patient_name || t.patient_email || "Preview patient"}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <Badge
                      tone={DISPOSITION_TONE[t.disposition] ?? "neutral"}
                      size="sm"
                    >
                      {t.disposition}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap">
                    {TICKET_STATE_LABELS[t.state] ?? t.state}
                  </td>
                  <td
                    className={`whitespace-nowrap ${overdue ? "text-danger font-semibold" : ""}`}
                  >
                    {overdue ? "overdue" : ageLabel(t.first_flagged_at)}
                  </td>
                  <td className="whitespace-nowrap text-ink-muted">
                    {t.source}
                  </td>
                  <td className="whitespace-nowrap">
                    {t.assigned_to
                      ? (nameById[t.assigned_to] ?? "assigned")
                      : "—"}
                  </td>
                </tr>
              );
            })}
            {tickets.length === 0 && (
              <tr>
                <td colSpan={8} className="text-ink-muted">
                  No open tickets.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
